import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, getDoc, Timestamp, deleteField, onSnapshot } from 'firebase/firestore';
import { db, notifyMatchConfirmed, shipMatchWithSagawa, getTrackingInfo } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from './ConfirmationDialog';
import PackageSizeSelector from './PackageSizeSelector';
import PickupDateSelector from './PickupDateSelector';
import { getCountryFlag, getCountryNameFromCode, countries as countryList } from '../utils/countryUtils';
import { DEFAULT_QUIKBAK_FEE } from '../utils/shippingUtils';
import { fetchShippingEstimate } from '../utils/shippingApi';

const PICKUP_TIME_SLOTS = {
    '08': '08:00-10:00',
    '09': '09:00-12:00',
    '10': '10:00-12:00',
    '12': '12:00-14:00',
    '14': '14:00-16:00',
    '16': '16:00-18:00',
    '18': '18:00-20:00',
    '19': '19:00-21:00'
};

const ZERO_DECIMAL_CURRENCIES = new Set([
    'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'vnd', 'xaf', 'xof', 'xpf'
]);

const formatCurrency = (amount, currency = 'JPY') => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
        return '—';
    }

    const normalizedCurrency = (currency || 'JPY').toUpperCase();
    const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has((currency || 'jpy').toLowerCase());
    const divisor = isZeroDecimal ? 1 : 100;

    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: normalizedCurrency,
        minimumFractionDigits: isZeroDecimal ? 0 : 2,
        maximumFractionDigits: isZeroDecimal ? 0 : 2
    }).format(amount / divisor);
};

const getStatusKey = (status) => {
    if (!status) return 'unknown';
    const mapping = {
        'on-hold': 'onHold',
        'waiting-for-payment': 'waitingForPayment',
        'ready-to-ship': 'readyToShip'
    };

    return mapping[status] || status;
};

export default function MatchDetails() {
    const { t } = useTranslation();
    const [match, setMatch] = useState(null);
    const [foundItem, setFoundItem] = useState(null);
    const [lostItem, setLostItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
    const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
    const [showPackageSizeSelector, setShowPackageSizeSelector] = useState(false);
    const [selectedPackageSize, setSelectedPackageSize] = useState(null);
    const { id, hotelId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [userClaims, setUserClaims] = useState(null);
    const [shippingQuote, setShippingQuote] = useState(null);
    const [shippingQuoteLoading, setShippingQuoteLoading] = useState(false);
    const [shippingQuoteError, setShippingQuoteError] = useState('');
    const [shippingRequestLoading, setShippingRequestLoading] = useState(false);
    const [showPickupDateDialog, setShowPickupDateDialog] = useState(false);
    const [selectedReturnMethod, setSelectedReturnMethod] = useState('postal');
    const [manualReturnDetails, setManualReturnDetails] = useState('');
    const [showDisposeDialog, setShowDisposeDialog] = useState(false);
    const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
    const [hotelData, setHotelData] = useState(null);
    const [trackingInfo, setTrackingInfo] = useState(null);
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [trackingError, setTrackingError] = useState('');
    const showPricing = !isStaffOrAdmin;

    useEffect(() => {
        async function checkUserRole() {
            if (currentUser) {
                const idTokenResult = await currentUser.getIdTokenResult();
                const isAdmin = idTokenResult.claims.admin === true;
                const isStaff = idTokenResult.claims.staff === true;
                setIsStaffOrAdmin(isAdmin || isStaff);
                setUserClaims(idTokenResult.claims);
            } else {
                setIsStaffOrAdmin(false);
                setUserClaims(null);
            }
        }
        checkUserRole();
    }, [currentUser]);

    useEffect(() => {
        if (!id) {
            return undefined;
        }

        setLoading(true);
        setError('');

        const matchRef = doc(db, 'matches', id);
        let unsubscribeFoundItem = null;
        let unsubscribeLostItem = null;
        let currentFoundId = '';
        let currentLostId = '';

        const unsubscribeMatch = onSnapshot(matchRef, (matchSnap) => {
            if (!matchSnap.exists()) {
                setMatch(null);
                setFoundItem(null);
                setLostItem(null);
                setError(t('matchDetails.messages.matchNotFound'));
                setLoading(false);
                if (unsubscribeFoundItem) {
                    unsubscribeFoundItem();
                    unsubscribeFoundItem = null;
                }
                if (unsubscribeLostItem) {
                    unsubscribeLostItem();
                    unsubscribeLostItem = null;
                }
                return;
            }

            const matchData = { id: matchSnap.id, ...matchSnap.data() };
            setMatch(matchData);
            setSelectedPackageSize(matchData.packageSize || null);
            const paymentComplete = matchData.paymentStatus === 'succeeded' || matchData.status === 'ready-to-ship' || matchData.status === 'shipped';
            setShowPackageSizeSelector(!paymentComplete && !matchData.packageSize && isStaffOrAdmin);
            setLoading(false);

            const nextFoundId = typeof matchData.foundItemId === 'string' ? matchData.foundItemId : '';
            if (nextFoundId !== currentFoundId) {
                if (unsubscribeFoundItem) {
                    unsubscribeFoundItem();
                    unsubscribeFoundItem = null;
                }
                currentFoundId = nextFoundId;

                if (nextFoundId) {
                    const foundRef = doc(db, 'found-items', nextFoundId);
                    unsubscribeFoundItem = onSnapshot(foundRef, (foundSnap) => {
                        if (foundSnap.exists()) {
                            setFoundItem({ id: foundSnap.id, ...foundSnap.data() });
                        } else {
                            setFoundItem(null);
                        }
                    }, (foundError) => {
                        console.error('Failed to listen to found item:', foundError);
                    });
                } else {
                    setFoundItem(null);
                }
            }

            const nextLostId = typeof matchData.lostItemId === 'string' ? matchData.lostItemId : '';
            if (nextLostId !== currentLostId) {
                if (unsubscribeLostItem) {
                    unsubscribeLostItem();
                    unsubscribeLostItem = null;
                }
                currentLostId = nextLostId;

                if (nextLostId) {
                    const lostRef = doc(db, 'lost-items', nextLostId);
                    unsubscribeLostItem = onSnapshot(lostRef, (lostSnap) => {
                        if (lostSnap.exists()) {
                            setLostItem({ id: lostSnap.id, ...lostSnap.data() });
                        } else {
                            setLostItem(null);
                        }
                    }, (lostError) => {
                        console.error('Failed to listen to lost item:', lostError);
                    });
                } else {
                    setLostItem(null);
                }
            }
        }, (matchError) => {
            console.error('Failed to listen to match data:', matchError);
            setError(t('matchDetails.messages.errorFetching') + matchError.message);
            setLoading(false);
        });

        return () => {
            unsubscribeMatch();
            if (unsubscribeFoundItem) {
                unsubscribeFoundItem();
            }
            if (unsubscribeLostItem) {
                unsubscribeLostItem();
            }
        };
    }, [id, isStaffOrAdmin, t]);

    const handleUnmatch = async () => {
        if (!isStaffOrAdmin || !canUnmatch) {
            setShowUnmatchDialog(false);
            return;
        }

        try {
            // Delete the match document
            await deleteDoc(doc(db, "matches", id));

            // Update the found item - remove match reference and set status to pending
            if (foundItem?.id) {
                const foundItemRef = doc(db, "found-items", foundItem.id);
                await updateDoc(foundItemRef, {
                    matchId: null,
                    status: 'pending'
                });
            }

            // Update the lost item - remove match reference and set status to pending
            if (lostItem?.id) {
                const lostItemRef = doc(db, "lost-items", lostItem.id);
                await updateDoc(lostItemRef, {
                    matchId: null,
                    status: 'pending'
                });
            }

            navigate(`/${hotelId}/admin`);
        } catch (err) {
            setError(t('matchDetails.messages.errorUnmatching') + err.message);
        } finally {
            setShowUnmatchDialog(false);
        }
    };

    const handlePackageSizeSelect = async (size, weight) => {
        if (!canEditPackage) {
            return;
        }
        try {
            // Get the country from the lost item
            const country = lostItem.country;
            if (!country) {
                console.error("No country specified in lost item");
                return;
            }

            const now = Timestamp.now();
            const matchRef = doc(db, "matches", id);
            const isFinalised = match?.status === 'ready-to-ship' || match?.status === 'shipped';
            const firestoreUpdate = {
                packageSize: size,
                updatedAt: now
            };

            const localUpdate = {
                packageSize: size,
                updatedAt: now
            };

            if (weight && weight.trim() !== '') {
                const parsedWeight = parseFloat(weight);
                if (!Number.isNaN(parsedWeight)) {
                    firestoreUpdate.packageWeight = parsedWeight;
                    localUpdate.packageWeight = parsedWeight;
                }
            }

            if (!isFinalised) {
                firestoreUpdate.status = 'waiting-for-payment';
                firestoreUpdate.paymentTotal = deleteField();
                firestoreUpdate.destinationCountry = deleteField();
                firestoreUpdate.destinationCountryCode = deleteField();
                firestoreUpdate.selectedShippingCountryName = deleteField();
                firestoreUpdate.selectedShippingCountryCode = deleteField();
                firestoreUpdate.shippingAddress = deleteField();
                firestoreUpdate.readyToShipCode = deleteField();

                localUpdate.status = 'waiting-for-payment';
            }

            await updateDoc(matchRef, firestoreUpdate);

            if (!isFinalised) {
                if (foundItem?.id) {
                    updateDoc(doc(db, "found-items", foundItem.id), {
                        status: 'waiting-for-payment',
                        updatedAt: now
                    }).catch((error) => console.warn('Failed to update found item status:', error));
                }

                if (lostItem?.id) {
                    updateDoc(doc(db, "lost-items", lostItem.id), {
                        status: 'waiting-for-payment',
                        updatedAt: now
                    }).catch((error) => console.warn('Failed to update lost item status:', error));
                }
            }

            setMatch(prev => {
                if (!prev) {
                    return prev;
                }
                const next = { ...prev, ...localUpdate };
                if (!isFinalised) {
                    next.status = 'waiting-for-payment';
                    next.paymentStatus = 'pending';
                    delete next.paymentTotal;
                    delete next.destinationCountry;
                    delete next.destinationCountryCode;
                    delete next.selectedShippingCountryName;
                    delete next.selectedShippingCountryCode;
                    delete next.shippingAddress;
                    delete next.readyToShipCode;
                }
                return next;
            });
            setSelectedPackageSize(size);
            setShippingQuote(null);
            setShippingQuoteError('');
            setShowPackageSizeSelector(false);
        } catch (error) {
            console.error("Error updating package size:", error);
        }
    };

    const handleSchedulePickup = async (pickupDate, pickupTimeSlot) => {
        if (!isStaffOrAdmin || shippingRequestLoading) {
            return;
        }

        setError('');
        setSuccessMessage('');
        setShippingRequestLoading(true);
        setShowPickupDateDialog(false);
        setShowRescheduleDialog(false);

        try {
            const response = await shipMatchWithSagawa({ matchId: id, pickupDate, pickupTimeSlot });
            const payload = response?.data || response || {};
            const tracking = payload?.shippingNumber ? ` ${t('matchDetails.messages.shippingTracking', { tracking: payload.shippingNumber })}` : '';
            setSuccessMessage(`${t('matchDetails.messages.pickupScheduled')}${tracking}`.trim());
            // Don't update local state - let the Firestore listener handle it
        } catch (err) {
            console.error('Error creating shipping label:', err);
            const message = err?.message || err?.details || '';
            setError(`${t('matchDetails.messages.errorShipping')}${message}`);
        } finally {
            setShippingRequestLoading(false);
        }
    };

    const handleConfirmCollected = async () => {
        if (!isStaffOrAdmin || shippingRequestLoading) return;

        setError('');
        setSuccessMessage('');
        setShippingRequestLoading(true);

        try {
            const matchRef = doc(db, 'matches', id);
            await updateDoc(matchRef, {
                status: 'shipped',
                collectedAt: Timestamp.now(),
                shippedAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
            setSuccessMessage(t('matchDetails.messages.packageCollected'));
            // Don't update local state - let the Firestore listener handle it
        } catch (error) {
            console.error("Error confirming collection:", error);
            setError(t('matchDetails.messages.errorCollecting') + error.message);
        } finally {
            setShippingRequestLoading(false);
        }
    };

    const handleConfirmMatch = async () => {
        if (!isStaffOrAdmin) return;

        // If dispose is selected, show confirmation dialog first
        if (selectedReturnMethod === 'dispose') {
            setShowDisposeDialog(true);
            return;
        }

        try {
            const matchRef = doc(db, "matches", id);
            const now = Timestamp.now();

            let updateData = {
                confirmedAt: now,
                updatedAt: now,
                returnType: selectedReturnMethod
            };

            // Handle different return types
            if (selectedReturnMethod === 'postal') {
                // Postal return - goes to confirmed status (existing flow)
                updateData.status = 'confirmed';
            } else {
                // Manual returns (in-person or group)
                updateData.status = 'manual-return';
                updateData.returnDetails = manualReturnDetails.trim();
                updateData.manualReturnAt = now;
            }

            await updateDoc(matchRef, updateData);

            // Send notification to customer only for postal returns
            if (selectedReturnMethod === 'postal') {
                await notifyMatchConfirmed({ matchId: id });
            }

            // Update local state
            setMatch(prev => ({
                ...prev,
                ...updateData
            }));

            // Show success message only for postal returns
            setError(null);
            if (selectedReturnMethod === 'postal') {
                setSuccessMessage(t('matchDetails.statusMessages.confirmMatchSuccess'));
            }
        } catch (error) {
            console.error("Error confirming match:", error);
            setError(t('matchDetails.statusMessages.errorConfirming') + error.message);
        }
    };

    const handleConfirmDispose = async () => {
        if (!isStaffOrAdmin) return;

        try {
            const matchRef = doc(db, "matches", id);
            const now = Timestamp.now();

            // Get disposer information
            let disposerName = '';
            let disposerEmail = currentUser?.email || '';
            let disposerId = currentUser?.uid || '';

            if (currentUser) {
                const userDocRef = doc(db, 'users', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    disposerName = userData.displayName || userData.name || '';
                }
            }

            if (!disposerName) {
                disposerName = currentUser?.displayName || currentUser?.email || 'Unknown User';
            }

            // Update the match status to disposed
            await updateDoc(matchRef, {
                status: 'disposed',
                disposedAt: now,
                disposer: disposerName,
                disposerEmail: disposerEmail,
                disposerId: disposerId,
                updatedAt: now
            });

            // Update local state
            setMatch(prev => ({
                ...prev,
                status: 'disposed',
                disposedAt: now,
                disposer: disposerName,
                disposerEmail: disposerEmail,
                disposerId: disposerId,
                updatedAt: now
            }));

            // Show success message
            setSuccessMessage('Item marked as disposed successfully');
            setShowDisposeDialog(false);
        } catch (error) {
            console.error("Error disposing item:", error);
            setError("Failed to dispose item: " + error.message);
            setShowDisposeDialog(false);
        }
    };

    const handleOnHold = async () => {
        if (!isStaffOrAdmin) return;

        try {
            // Update match status to on-hold
            const matchRef = doc(db, "matches", id);
            await updateDoc(matchRef, {
                status: 'on-hold',
                onHoldAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            // Update local state
            setMatch(prev => ({
                ...prev,
                status: 'on-hold',
                onHoldAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            }));

            // Show success message
            setError(null);
            setSuccessMessage(t('matchDetails.statusMessages.onHoldSuccess'));
        } catch (error) {
            console.error("Error placing match on hold:", error);
            setError(t('matchDetails.statusMessages.errorOnHold') + error.message);
        }
    };

    // Dummy postage cost calculation based on size and country
    // Format date for display
    const formatDate = (date) => {
        if (!date) return t('matchDetails.values.noData');

        try {
            // If it's a Firestore Timestamp
            if (date instanceof Timestamp) {
                return date.toDate().toLocaleDateString();
            }
            // If it's already a Date object
            if (date instanceof Date) {
                return date.toLocaleDateString();
            }
            // If it's a string or timestamp object from the database
            return new Date(date).toLocaleDateString();
        } catch (e) {
            console.error('Error formatting date:', e);
            return t('matchDetails.values.invalidDate');
        }
    };

    const fallbackCountryName = lostItem?.country || null;
    const fallbackCountryCode = fallbackCountryName
        ? (countryList.find((c) => c.name === fallbackCountryName)?.isoCode || '').toUpperCase()
        : '';

    const isPaymentFinalised = match?.paymentStatus === 'succeeded' || match?.status === 'ready-to-ship' || match?.status === 'shipped';
    const canUnmatch = Boolean(isStaffOrAdmin && match && !isPaymentFinalised);
    const canEditPackage = Boolean((userClaims?.admin || userClaims?.staff) && match && !isPaymentFinalised);

    useEffect(() => {
        if (!canUnmatch && showUnmatchDialog) {
            setShowUnmatchDialog(false);
        }
    }, [canUnmatch, showUnmatchDialog]);

    useEffect(() => {
        if (isStaffOrAdmin && match && !match.packageSize) {
            setShowPackageSizeSelector(true);
        }
    }, [isStaffOrAdmin, match?.packageSize]);

    // Fetch hotel data to get default pickup time slot
    useEffect(() => {
        if (!match?.hotelId) {
            setHotelData(null);
            return;
        }

        const hotelRef = doc(db, 'hotels', match.hotelId);
        getDoc(hotelRef).then((hotelSnap) => {
            if (hotelSnap.exists()) {
                setHotelData(hotelSnap.data());
            }
        }).catch((err) => {
            console.error('Failed to fetch hotel data:', err);
        });
    }, [match?.hotelId]);

    // Fetch tracking info when tracking number is available
    useEffect(() => {
        const trackingNumber = match?.shippingLabel?.shippingNumber || match?.shippingTrackingNumber;

        if (!trackingNumber) {
            setTrackingInfo(null);
            setTrackingError('');
            return;
        }

        // Only fetch for shipped or scheduled-for-pickup status
        if (match?.status !== 'shipped' && match?.status !== 'scheduled-for-pickup') {
            return;
        }

        const fetchTracking = async () => {
            setTrackingLoading(true);
            setTrackingError('');

            try {
                const response = await getTrackingInfo({ trackingNumber });
                const data = response?.data || response;

                if (data.success) {
                    setTrackingInfo(data);
                } else {
                    setTrackingError(data.error || t('matchDetails.tracking.errorFetching'));
                }
            } catch (err) {
                console.error('Failed to fetch tracking info:', err);
                setTrackingError(t('matchDetails.tracking.errorFetching'));
            } finally {
                setTrackingLoading(false);
            }
        };

        fetchTracking();
    }, [match?.shippingLabel?.shippingNumber, match?.shippingTrackingNumber, match?.status, t]);

    useEffect(() => {
        if (!isStaffOrAdmin) {
            setShippingQuote(null);
            setShippingQuoteError('');
            setShippingQuoteLoading(false);
            return;
        }

        if (!match?.id || !match?.packageSize) {
            setShippingQuote(null);
            return;
        }

    const preferredCountryName =
            (match?.shippingAddress?.country && match.shippingAddress.country.trim() !== '') ? match.shippingAddress.country :
            (match?.selectedShippingCountryName && match.selectedShippingCountryName.trim() !== '') ? match.selectedShippingCountryName :
            (match?.destinationCountry && match.destinationCountry.trim() !== '') ? match.destinationCountry :
            fallbackCountryName ||
            null;

        if (!preferredCountryName) {
            setShippingQuote(null);
            return;
        }

        const preferredCountryCode =
            (match?.shippingAddress?.countryCode && match.shippingAddress.countryCode.trim() !== '') ? match.shippingAddress.countryCode :
            (match?.selectedShippingCountryCode && match.selectedShippingCountryCode.trim() !== '') ? match.selectedShippingCountryCode :
            (match?.destinationCountryCode && match.destinationCountryCode.trim() !== '') ? match.destinationCountryCode :
            fallbackCountryCode ||
            '';

        let ignore = false;
        setShippingQuoteLoading(true);
        setShippingQuoteError('');

        fetchShippingEstimate({
            matchId: match.id,
            countryName: preferredCountryName,
            countryCode: preferredCountryCode,
            currency: match?.paymentCurrency || 'jpy',
        })
            .then((quote) => {
                if (!ignore) {
                    setShippingQuote(quote);
                }
            })
            .catch((error) => {
                if (!ignore) {
                    console.error('Failed to fetch shipping estimate for match:', error);
                    setShippingQuote(null);
                    setShippingQuoteError(error?.message || t('payment.messages.error'));
                }
            })
            .finally(() => {
                if (!ignore) {
                    setShippingQuoteLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
    }, [fallbackCountryCode, fallbackCountryName, isStaffOrAdmin, match?.destinationCountry, match?.destinationCountryCode, match?.id, match?.packageSize, match?.paymentCurrency, match?.selectedShippingCountryCode, match?.selectedShippingCountryName, match?.shippingAddress?.country, match?.shippingAddress?.countryCode, t]);

    const shippingCostValue = shippingQuote?.shippingCost ?? null;

    const quikbakFeeValue = shippingQuote?.quikbakFee ?? DEFAULT_QUIKBAK_FEE;

    const totalChargeValue = typeof match?.paymentTotal === 'number'
        ? match.paymentTotal
        : (shippingQuote?.totalAmount ?? null);

    const destinationCountryCode = shippingQuote?.countryCode
        || match?.shippingAddress?.countryCode
        || match?.destinationCountryCode
        || match?.selectedShippingCountryCode
        || fallbackCountryCode;

    const destinationCountryName = shippingQuote?.countryName
        || getCountryNameFromCode(destinationCountryCode)
        || match?.shippingAddress?.country
        || match?.destinationCountry
        || match?.selectedShippingCountryName
        || fallbackCountryName
        || t('matchDetails.values.noData');
    const destinationCountryForFlag = destinationCountryCode || match?.destinationCountry || match?.selectedShippingCountryCode || fallbackCountryCode;

    if (loading) {
        return <div className="loading-container">{t('matchDetails.loading')}</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    if (!loading && !error) {
        console.log("Package section condition:", (showPackageSizeSelector || (match && match.packageSize)) && (userClaims?.admin || userClaims?.staff));
    }

    return (
        <div className="item-details-container">
            <div className="item-details-header">
                <button onClick={() => navigate(`/${hotelId}/admin`)} className="back-button">
                    &larr; {t('matchDetails.doneButton')}
                </button>
                <h2>{t('matchDetails.title')}</h2>
                {isStaffOrAdmin && canUnmatch && (
                    <div className="match-action-buttons">
                        <button
                            className="unmatch-button"
                            onClick={() => setShowUnmatchDialog(true)}
                        >
                            {t('matchDetails.buttons.unmatchItems')}
                        </button>
                    </div>
                )}
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
                <div className="success-message">
                    {successMessage}
                    <button onClick={() => setSuccessMessage('')}>✕</button>
                </div>
            )}
            {error && (
                <div className="error-message">
                    {error}
                    <button onClick={() => setError('')}>✕</button>
                </div>
            )}

            {/* Prominent Pickup Code Banner */}
            {match?.readyToShipCode && (match?.status === 'ready-to-ship' || match?.status === 'scheduled-for-pickup' || match?.status === 'shipped') && (
                <div className="pickup-code-banner" style={{
                    backgroundColor: '#e3f2fd',
                    border: '2px solid #1976d2',
                    borderRadius: '12px',
                    padding: '20px 24px',
                    marginBottom: '24px',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '0.9rem', color: '#1565c0', marginBottom: '8px', fontWeight: '500' }}>
                        {t('matchDetails.fields.pickupCode')}
                    </div>
                    <div style={{
                        fontSize: '2.5rem',
                        fontWeight: '700',
                        color: '#1976d2',
                        letterSpacing: '0.15em',
                        fontFamily: 'monospace'
                    }}>
                        {match.readyToShipCode}
                    </div>
                </div>
            )}

            {match && foundItem && lostItem && (
                <div className="match-details-content">
                    <div className="match-info">
                        <h3>{t('matchDetails.sections.matchInformation')}</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <div className="detail-label">{t('matchDetails.fields.status')}</div>
                                <div className={`detail-value status-badge status-${match.status}`}>
                                    {t(`matchDetails.values.${getStatusKey(match.status)}`, {
                                        defaultValue: match.status || t('matchDetails.values.unknown')
                                    })}
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label">{t('matchDetails.fields.matchedOn')}</div>
                                <div className="detail-value">
                                    {match.matchedAt ? new Date(match.matchedAt.toDate()).toLocaleDateString() : t('matchDetails.values.noData')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="matched-items-container">
                        {/* Found Item section */}
                        <div className="item-card found-item">
                            <div className="item-card-header">
                                <h3 onClick={() => navigate(`/${hotelId}/found-items/${foundItem.id}`)} style={{ cursor: 'pointer', color: '#2196f3' }}>
                                    {t('matchDetails.sections.foundItem')}
                                    <span style={{ fontSize: '0.8em', marginLeft: '5px' }}>→</span>
                                </h3>
                            </div>
                            <div className="detail-section">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.itemType')}</div>
                                        <div className="detail-value">{foundItem.itemType || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.itemName')}</div>
                                        <div className="detail-value">{foundItem.itemName || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.dateFound')}</div>
                                        <div className="detail-value">{formatDate(foundItem.dateFound)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.locationFound')}</div>
                                        <div className="detail-value">{foundItem.locationFound || foundItem.locationLost || t('matchDetails.values.unknown')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.finder')}</div>
                                        <div className="detail-value">{foundItem.finderName || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item full-width">
                                        <div className="detail-label">{t('matchDetails.fields.comments')}</div>
                                        <div className="detail-value comment-text">{foundItem.comments || t('matchDetails.values.noComments')}</div>
                                    </div>
                                </div>

                                {/* Add image display */}
                                {foundItem.imageUrls && foundItem.imageUrls.length > 0 && (
                                    <div className="detail-item full-width">
                                        <div className="detail-label">{t('matchDetails.fields.photos')}</div>
                                        <div className="image-preview-container">
                                            {foundItem.imageUrls.map((url, index) => (
                                                <div key={index} className="image-preview">
                                                    <img src={url} alt={`Found item photo ${index + 1}`} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Lost Item section */}
                        <div className="item-card lost-item">
                            <div className="item-card-header">
                                <h3 onClick={() => navigate(`/${hotelId}/lost-items/${lostItem.id}`)} style={{ cursor: 'pointer', color: '#2196f3' }}>
                                    {t('matchDetails.sections.lostItem')}
                                    <span style={{ fontSize: '0.8em', marginLeft: '5px' }}>→</span>
                                </h3>
                            </div>
                            <div className="detail-section">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.itemType')}</div>
                                        <div className="detail-value">{lostItem.itemType || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.itemName')}</div>
                                        <div className="detail-value">{lostItem.itemName || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.dateLost')}</div>
                                        <div className="detail-value">{formatDate(lostItem.dateLost)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.locationLost')}</div>
                                        <div className="detail-value">{lostItem.locationLost || t('matchDetails.values.unknown')}</div>
                                    </div>
                                    <div className="detail-item full-width">
                                        <div className="detail-label">{t('matchDetails.fields.description')}</div>
                                        <div className="detail-value comment-text">{lostItem.description || t('matchDetails.values.noDescription')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.contactName')}</div>
                                        <div className="detail-value">{`${lostItem.firstName || ''} ${lostItem.surname || ''}`.trim() || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.contactPhone')}</div>
                                        <div className="detail-value">{lostItem.phone || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.contactEmail')}</div>
                                        <div className="detail-value">{lostItem.email || t('matchDetails.values.noData')}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label">{t('matchDetails.fields.country')}</div>
                                        <div className="detail-value">
                                            {lostItem.country ? (
                                                <span style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                                    {getCountryFlag(lostItem.country)}
                                                </span>
                                            ) : (
                                                <span style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                                    {getCountryFlag()}
                                                </span>
                                            )}
                                            {lostItem.country || <span className="no-data">{t('matchDetails.values.notProvided')}</span>}
                                        </div>
                                    </div>
                                </div>

                                {/* Add image display */}
                                {lostItem.imageUrls && lostItem.imageUrls.length > 0 && (
                                    <div className="detail-item full-width">
                                        <div className="detail-label">{t('matchDetails.fields.photos')}</div>
                                        <div className="image-preview-container">
                                            {lostItem.imageUrls.map((url, index) => (
                                                <div key={index} className="image-preview">
                                                    <img src={url} alt={`Lost item photo ${index + 1}`} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Package Information */}
                    {/* Show package info section if user is staff/admin, regardless of whether package size exists */}
                    {(userClaims?.admin || userClaims?.staff) && (
                        <div className="package-information">
                            {/* Return Method Selection - only show for matched status */}
                            {match.status === 'matched' && (
                                <div className="detail-section">
                                    <h3>{t('matchDetails.returnType.title')}</h3>

                                    {/* Return Method Buttons */}
                                    <div className="return-method-buttons">
                                        <button
                                            className={`return-method-btn ${selectedReturnMethod === 'postal' ? 'active' : ''}`}
                                            onClick={() => setSelectedReturnMethod('postal')}
                                        >
                                            {t('matchDetails.returnType.postal')}
                                        </button>
                                        <button
                                            className={`return-method-btn ${selectedReturnMethod === 'in-person' ? 'active' : ''}`}
                                            onClick={() => setSelectedReturnMethod('in-person')}
                                        >
                                            {t('matchDetails.returnType.inPerson')}
                                        </button>
                                        <button
                                            className={`return-method-btn ${selectedReturnMethod === 'group' ? 'active' : ''}`}
                                            onClick={() => setSelectedReturnMethod('group')}
                                        >
                                            {t('matchDetails.returnType.group')}
                                        </button>
                                        <button
                                            className={`return-method-btn ${selectedReturnMethod === 'dispose' ? 'active' : ''}`}
                                            onClick={() => setSelectedReturnMethod('dispose')}
                                        >
                                            {t('matchDetails.returnType.dispose')}
                                        </button>
                                    </div>

                                    {/* Show Package Size Selector for postal */}
                                    {selectedReturnMethod === 'postal' && (
                                        <div style={{ marginTop: '20px' }}>
                                            <PackageSizeSelector
                                                onSelect={handlePackageSizeSelect}
                                                selectedSize={selectedPackageSize}
                                            />
                                        </div>
                                    )}

                                    {/* Show Details Input for manual returns */}
                                    {(selectedReturnMethod === 'in-person' || selectedReturnMethod === 'group') && (
                                        <div style={{ marginTop: '20px' }}>
                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                                {selectedReturnMethod === 'in-person'
                                                    ? t('matchDetails.returnType.inPersonDetailsPrompt')
                                                    : t('matchDetails.returnType.groupDetailsPrompt')}
                                            </label>
                                            <textarea
                                                value={manualReturnDetails}
                                                onChange={(e) => setManualReturnDetails(e.target.value)}
                                                placeholder={t('matchDetails.returnType.detailsPlaceholder')}
                                                rows={4}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontFamily: 'inherit',
                                                    fontSize: '0.95rem',
                                                    resize: 'vertical'
                                                }}
                                            />
                                            <div style={{ marginTop: '16px' }}>
                                                <button
                                                    className="confirm-button"
                                                    onClick={handleConfirmMatch}
                                                >
                                                    {t('matchDetails.buttons.confirmReturn')}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Show Dispose confirmation button */}
                                    {selectedReturnMethod === 'dispose' && (
                                        <div style={{ marginTop: '20px' }}>
                                            <p style={{ marginBottom: '16px', color: '#666' }}>
                                                {t('matchDetails.returnType.disposeDescription')}
                                            </p>
                                            <div style={{ marginTop: '16px' }}>
                                                <button
                                                    className="confirm-button"
                                                    onClick={handleConfirmMatch}
                                                    style={{ backgroundColor: '#d32f2f' }}
                                                >
                                                    {t('matchDetails.buttons.confirmDispose')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Show manual return information for manual-return status */}
                            {match.status === 'manual-return' && (
                                <div className="detail-section">
                                    <h3>{t('matchDetails.sections.packageInformation')}</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <div className="detail-label">{t('adminDashboard.table.headers.returnType')}</div>
                                            <div className="detail-value status-badge">
                                                {match.returnType === 'in-person'
                                                    ? t('matchDetails.returnType.inPerson')
                                                    : t('matchDetails.returnType.group')}
                                            </div>
                                        </div>
                                        {match.returnDetails && (
                                            <div className="detail-item full-width">
                                                <div className="detail-label">{t('adminDashboard.table.headers.returnDetails')}</div>
                                                <div className="detail-value" style={{ whiteSpace: 'pre-wrap' }}>
                                                    {match.returnDetails}
                                                </div>
                                            </div>
                                        )}
                                        {match.manualReturnAt && (
                                            <div className="detail-item">
                                                <div className="detail-label">{t('adminDashboard.table.headers.returnedDate')}</div>
                                                <div className="detail-value">
                                                    {match.manualReturnAt?.toDate
                                                        ? new Date(match.manualReturnAt.toDate()).toLocaleDateString()
                                                        : t('matchDetails.values.invalidDate')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {canEditPackage && (
                                        <div className="action-buttons" style={{ marginTop: '16px' }}>
                                            <button
                                                className="edit-package-button"
                                                onClick={async () => {
                                                    // Reset to matched status to allow re-selection
                                                    try {
                                                        const matchRef = doc(db, "matches", id);
                                                        await updateDoc(matchRef, {
                                                            status: 'matched',
                                                            returnType: deleteField(),
                                                            returnDetails: deleteField(),
                                                            manualReturnAt: deleteField(),
                                                            confirmedAt: deleteField()
                                                        });
                                                        setSelectedReturnMethod('postal');
                                                        setManualReturnDetails('');
                                                    } catch (error) {
                                                        console.error("Error resetting return type:", error);
                                                        setError("Failed to change return type: " + error.message);
                                                    }
                                                }}
                                            >
                                                {t('matchDetails.buttons.changeReturnType')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Show disposed information for disposed status */}
                            {match.status === 'disposed' && (
                                <div className="detail-section">
                                    <h3>{t('matchDetails.sections.packageInformation')}</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <div className="detail-label">{t('matchDetails.fields.status')}</div>
                                            <div className="detail-value status-badge status-disposed">
                                                {t('matchDetails.values.disposed')}
                                            </div>
                                        </div>
                                        {match.disposedAt && (
                                            <div className="detail-item">
                                                <div className="detail-label">{t('lostItemDetails.fields.disposedDate')}</div>
                                                <div className="detail-value">{formatDate(match.disposedAt)}</div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Allow changing return type if user has permission */}
                                    {canEditPackage && (
                                        <div className="action-buttons" style={{ marginTop: '16px' }}>
                                            <button
                                                className="edit-package-button"
                                                onClick={async () => {
                                                    // Reset to matched status to allow re-selection
                                                    try {
                                                        const matchRef = doc(db, "matches", id);
                                                        await updateDoc(matchRef, {
                                                            status: 'matched',
                                                            disposedAt: deleteField(),
                                                            confirmedAt: deleteField()
                                                        });
                                                        setSelectedReturnMethod('postal');
                                                    } catch (error) {
                                                        console.error("Error resetting return type:", error);
                                                        setError("Failed to change return type: " + error.message);
                                                    }
                                                }}
                                            >
                                                {t('matchDetails.buttons.changeReturnType')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Show package information after confirmation - only for postal returns */}
                            {match.status !== 'matched' && match.status !== 'manual-return' && match.status !== 'disposed' && (
                                <div className="detail-section">
                                    <h3>{t('matchDetails.sections.packageInformation')}</h3>
                                    {match.packageSize ? (
                                        <>
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <div className="detail-label">{t('matchDetails.fields.packageSize')}</div>
                                                    <div className="detail-value status-badge">{match.packageSize}</div>
                                                </div>
                                                {match.packageWeight && (
                                                    <div className="detail-item">
                                                        <div className="detail-label">{t('matchDetails.fields.packageWeight')}</div>
                                                        <div className="detail-value">{match.packageWeight} kg</div>
                                                    </div>
                                                )}
                                                <div className="detail-item">
                                                    <div className="detail-label">{t('matchDetails.fields.destination')}</div>
                                                    <div className="detail-value">
                                                        {destinationCountryForFlag && (
                                                            <span style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                                                {getCountryFlag(destinationCountryForFlag)}
                                                            </span>
                                                        )}
                                                        {destinationCountryName}
                                                    </div>
                                                </div>
                                                {match.readyToShipCode && (
                                                    <div className="detail-item">
                                                        <div className="detail-label">{t('matchDetails.fields.pickupCode')}</div>
                                                        <div className="detail-value">{match.readyToShipCode}</div>
                                                    </div>
                                                )}
                                                {showPricing && (
                                                    <>
                                                        <div className="detail-item">
                                                            <div className="detail-label">{t('matchDetails.fields.shippingCost')}</div>
                                                            <div className="detail-value">
                                                                {shippingQuoteLoading
                                                                    ? t('payment.messages.loading')
                                                                    : (typeof shippingCostValue === 'number' && shippingCostValue > 0
                                                                        ? formatCurrency(shippingCostValue, match.paymentCurrency || 'JPY')
                                                                        : shippingQuoteError || t('matchDetails.values.noData'))}
                                                            </div>
                                                        </div>
                                                        <div className="detail-item">
                                                            <div className="detail-label">{t('matchDetails.fields.quikbakFee')}</div>
                                                            <div className="detail-value">
                                                                {typeof quikbakFeeValue === 'number'
                                                                    ? formatCurrency(quikbakFeeValue, match.paymentCurrency || 'JPY')
                                                                    : t('matchDetails.values.noData')}
                                                            </div>
                                                        </div>
                                                        <div className="detail-item">
                                                            <div className="detail-label">{t('matchDetails.fields.totalCharge')}</div>
                                                            <div className="detail-value">
                                                                {typeof totalChargeValue === 'number'
                                                                    ? formatCurrency(totalChargeValue, match.paymentCurrency || 'JPY')
                                                                    : t('matchDetails.values.noData')}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {(match.paymentStatus || match.status === 'waiting-for-payment') && (
                                                    <div className="detail-item">
                                                        <div className="detail-label">{t('matchDetails.fields.paymentStatus')}</div>
                                                        <div className="detail-value">
                                                            <span className={`status-badge status-${match.paymentStatus || 'pending'}`}>
                                                                {t(`payment.statusLabels.${match.paymentStatus || (match.status === 'waiting-for-payment' ? 'pending' : 'unknown')}`, {
                                                                    defaultValue: match.paymentStatus || (match.status === 'waiting-for-payment' ? t('payment.statusLabels.pending') : t('payment.messages.statusUnknown'))
                                                                })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {showPricing && (
                                                    <div className="detail-item">
                                                        <div className="detail-label">{t('matchDetails.fields.paymentAmount')}</div>
                                                        <div className="detail-value">
                                                            {typeof match.paymentAmount === 'number'
                                                                ? formatCurrency(match.paymentAmount, match.paymentCurrency || 'JPY')
                                                                : (totalChargeValue
                                                                    ? formatCurrency(totalChargeValue, match.paymentCurrency || 'JPY')
                                                                    : t('matchDetails.values.noData'))}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="detail-item">
                                                    <div className="detail-label">{t('matchDetails.fields.status')}</div>
                                                    <div className="detail-value">
                                                        <span className={`status-badge status-${match.status}`}>
                                                            {t(`matchDetails.values.${getStatusKey(match.status)}`, {
                                                                defaultValue: match.status || t('matchDetails.values.unknown')
                                                            })}
                                                        </span>
                                                        {match.shippedAt && (
                                                            <div className="shipped-date">
                                                                {t('matchDetails.fields.shippedOn')}: {new Date(match.shippedAt.toDate()).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {match.shippingAddress && (
                                                <>
                                                    <div className="detail-item full-width">
                                                        <div className="detail-label">{t('matchDetails.fields.shippingAddress')}</div>
                                                        <div className="detail-value" style={{ lineHeight: '1.6' }}>
                                                            <div>{match.shippingAddress.name}</div>
                                                            <div>{match.shippingAddress.line1}</div>
                                                            {match.shippingAddress.line2 && match.shippingAddress.line2.trim() !== '' && (
                                                                <div>{match.shippingAddress.line2}</div>
                                                            )}
                                                            <div>
                                                                {[match.shippingAddress.city, match.shippingAddress.state]
                                                                    .filter(Boolean)
                                                                    .join(', ')}{match.shippingAddress.postalCode ? ` ${match.shippingAddress.postalCode}` : ''}
                                                            </div>
                                                            <div>
                                                                <span style={{ marginRight: '6px' }}>
                                                                    {getCountryFlag(match.shippingAddress.countryCode || match.shippingAddress.country)}
                                                                </span>
                                                                {getCountryNameFromCode(match.shippingAddress.countryCode) || match.shippingAddress.country}
                                                            </div>
                                                            {match.shippingAddress.phone && match.shippingAddress.phone.trim() !== '' && (
                                                                <div>{match.shippingAddress.phone}</div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Shipping Status and Tracking */}
                                                    {match.shippingLabel && (
                                                        <div className="detail-item full-width" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                                                            <div className="detail-label" style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>
                                                                {t('matchDetails.fields.shippingLabel')}
                                                            </div>
                                                            <div className="detail-grid">
                                                                {match.status && (match.status === 'scheduled-for-pickup' || match.status === 'shipped') && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.status')}</div>
                                                                        <div className="detail-value">
                                                                            {match.status === 'scheduled-for-pickup'
                                                                                ? t('matchDetails.values.scheduledForPickup')
                                                                                : t('matchDetails.values.shipped')}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {match.shippingLabel.shippingNumber && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.trackingNumber')}</div>
                                                                        <div className="detail-value">
                                                                            {match.shippingLabel.shippingNumber}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {match.scheduledPickupDate && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.scheduledPickupDate')}</div>
                                                                        <div className="detail-value">{match.scheduledPickupDate}</div>
                                                                    </div>
                                                                )}
                                                                {match.scheduledPickupTimeSlot && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.scheduledPickupTime')}</div>
                                                                        <div className="detail-value">
                                                                            {PICKUP_TIME_SLOTS[match.scheduledPickupTimeSlot] || match.scheduledPickupTimeSlot}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {match.shippedAt && match.status === 'shipped' && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.shippedDate')}</div>
                                                                        <div className="detail-value">{formatDate(match.shippedAt)}</div>
                                                                    </div>
                                                                )}
                                                                {match.shippingLabel.printRequestId && (
                                                                    <div className="detail-item">
                                                                        <div className="detail-label">{t('matchDetails.fields.printRequestId')}</div>
                                                                        <div className="detail-value">{match.shippingLabel.printRequestId}</div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Live Tracking Status */}
                                                            {match.shippingLabel.shippingNumber && (
                                                                <div className="tracking-status-section" style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                                                    <div className="detail-label" style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {t('matchDetails.tracking.title')}
                                                                        {trackingLoading && (
                                                                            <span className="tracking-loading-spinner" style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid #ddd', borderTopColor: '#007bff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                                                                        )}
                                                                    </div>

                                                                    {trackingLoading && (
                                                                        <div className="tracking-loading" style={{ color: '#666', fontSize: '0.9rem' }}>
                                                                            {t('matchDetails.tracking.loading')}
                                                                        </div>
                                                                    )}

                                                                    {trackingError && !trackingLoading && (
                                                                        <div className="tracking-error" style={{ color: '#dc3545', fontSize: '0.9rem' }}>
                                                                            {trackingError}
                                                                        </div>
                                                                    )}

                                                                    {trackingInfo && !trackingLoading && (
                                                                        <div className="tracking-info">
                                                                            {/* Status Badge */}
                                                                            <div className="tracking-status" style={{ marginBottom: '12px' }}>
                                                                                <span className={`tracking-status-badge status-${trackingInfo.status}`} style={{
                                                                                    display: 'inline-block',
                                                                                    padding: '4px 12px',
                                                                                    borderRadius: '16px',
                                                                                    fontSize: '0.85rem',
                                                                                    fontWeight: '600',
                                                                                    backgroundColor: trackingInfo.status === 'delivered' ? '#28a745' :
                                                                                                     trackingInfo.status === 'in_transit' ? '#17a2b8' :
                                                                                                     trackingInfo.status === 'out_for_delivery' ? '#ffc107' :
                                                                                                     '#6c757d',
                                                                                    color: trackingInfo.status === 'out_for_delivery' ? '#212529' : '#fff'
                                                                                }}>
                                                                                    {t(`matchDetails.tracking.status.${trackingInfo.status}`, { defaultValue: trackingInfo.statusOriginal || trackingInfo.status })}
                                                                                </span>
                                                                            </div>

                                                                            {/* Render raw HTML from Sagawa in iframe */}
                                                                            {trackingInfo.detailHtml && (
                                                                                <iframe
                                                                                    title="Sagawa Tracking Details"
                                                                                    srcDoc={trackingInfo.detailHtml}
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        minHeight: '400px',
                                                                                        border: '1px solid #e9ecef',
                                                                                        borderRadius: '8px',
                                                                                        marginTop: '12px',
                                                                                        backgroundColor: '#fff'
                                                                                    }}
                                                                                    sandbox="allow-same-origin"
                                                                                />
                                                                            )}

                                                                            <a
                                                                                href={`https://k2k.sagawa-exp.co.jp/p/sagawa/web/okurijoinput.jsp`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                onClick={() => {
                                                                                    // Copy tracking number to clipboard before opening Sagawa site
                                                                                    const trackingNum = match?.shippingLabel?.shippingNumber || match?.shippingTrackingNumber;
                                                                                    if (trackingNum && navigator.clipboard) {
                                                                                        navigator.clipboard.writeText(trackingNum);
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    display: 'inline-block',
                                                                                    marginTop: '12px',
                                                                                    fontSize: '0.85rem',
                                                                                    color: '#007bff',
                                                                                    textDecoration: 'none'
                                                                                }}
                                                                            >
                                                                                {t('matchDetails.tracking.viewOnSagawa')} →
                                                                            </a>
                                                                            <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>
                                                                                {t('matchDetails.tracking.copiedHint')}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Edit Package Button - Only show if not shipped */}
                                            {canEditPackage && (
                                                <div className="action-buttons">
                                                    <button
                                                        className="edit-package-button"
                                                        onClick={async () => {
                                                            if (!canEditPackage) return;
                                                            // Reset to matched status to allow re-selection
                                                            try {
                                                                const matchRef = doc(db, "matches", id);
                                                                await updateDoc(matchRef, {
                                                                    status: 'matched',
                                                                    returnType: deleteField(),
                                                                    returnDetails: deleteField(),
                                                                    manualReturnAt: deleteField(),
                                                                    confirmedAt: deleteField(),
                                                                    packageSize: deleteField(),
                                                                    packageWeight: deleteField()
                                                                });
                                                                setSelectedReturnMethod('postal');
                                                                setManualReturnDetails('');
                                                                setSelectedPackageSize(null);
                                                            } catch (error) {
                                                                console.error("Error resetting return type:", error);
                                                                setError("Failed to edit package details: " + error.message);
                                                            }
                                                        }}
                                                    >
                                                        {t('matchDetails.buttons.editPackageDetails')}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        canEditPackage ? (
                                            <div style={{ marginTop: '20px' }}>
                                                <PackageSizeSelector
                                                    onSelect={handlePackageSizeSelect}
                                                    selectedSize={selectedPackageSize}
                                                />
                                            </div>
                                        ) : null
                                    )}
                                    
                                    {/* Schedule pickup or confirm collection */}
                                    {match.packageSize && match.status === 'ready-to-ship' && (
                                        <div className="action-buttons">
                                            <button
                                                className="shipped-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowPickupDateDialog(true);
                                                }}
                                                disabled={shippingRequestLoading}
                                            >
                                                {shippingRequestLoading
                                                    ? t('matchDetails.buttons.schedulePickupLoading')
                                                    : t('matchDetails.buttons.schedulePickup')}
                                            </button>
                                        </div>
                                    )}

                                    {match.packageSize && match.status === 'scheduled-for-pickup' && (
                                        <div className="action-buttons">
                                            <button
                                                className="reschedule-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowRescheduleDialog(true);
                                                }}
                                                disabled={shippingRequestLoading}
                                            >
                                                {t('matchDetails.buttons.reschedulePickup', 'Reschedule Pickup')}
                                            </button>
                                            <button
                                                className="shipped-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleConfirmCollected();
                                                }}
                                                disabled={shippingRequestLoading}
                                            >
                                                {shippingRequestLoading
                                                    ? t('matchDetails.buttons.confirmingCollection')
                                                    : t('matchDetails.buttons.confirmCollected')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <ConfirmationDialog
                isOpen={showUnmatchDialog}
                onClose={() => setShowUnmatchDialog(false)}
                onConfirm={handleUnmatch}
                title={t('matchDetails.confirmations.unmatchTitle')}
                message={t('matchDetails.confirmations.unmatchMessage')}
            />

            <PickupDateSelector
                isOpen={showPickupDateDialog}
                onClose={() => setShowPickupDateDialog(false)}
                onConfirm={handleSchedulePickup}
                loading={shippingRequestLoading}
                defaultTimeSlot={hotelData?.shipping?.sagawa?.shukaIraiTimeCode || '09'}
            />

            <PickupDateSelector
                isOpen={showRescheduleDialog}
                onClose={() => setShowRescheduleDialog(false)}
                onConfirm={handleSchedulePickup}
                loading={shippingRequestLoading}
                defaultTimeSlot={match?.scheduledPickupTimeSlot || hotelData?.shipping?.sagawa?.shukaIraiTimeCode || '09'}
                isReschedule={true}
            />

            <ConfirmationDialog
                isOpen={showDisposeDialog}
                onClose={() => setShowDisposeDialog(false)}
                onConfirm={handleConfirmDispose}
                title={t('matchDetails.confirmations.disposeTitle')}
                message={t('matchDetails.confirmations.disposeMessage')}
            />
        </div>
    );
} 
